import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface NavbarProps {
  transparent?: boolean;
}

export default function Navbar({ transparent = true }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const isHome = location.pathname === '/';

  const navLinks = [
    { label: '首页', path: '/' },
    { label: '分类', path: '/category/romance' },
    { label: '排行榜', path: '/#ranking' },
    { label: '最新更新', path: '/#latest' },
  ];

  const isTransparentBg = transparent && isHome && !scrolled;

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isTransparentBg
          ? 'bg-transparent'
          : 'bg-background-50/95 backdrop-blur-md'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <div className="flex items-center gap-8">
            <Link to="/" className="flex items-center gap-2">
              <span className={`font-heading text-2xl md:text-3xl font-bold tracking-tight transition-colors duration-300 ${
                isTransparentBg ? 'text-white' : 'text-foreground-950'
              }`}>
                ManhwaFun
              </span>
            </Link>
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    location.pathname === link.path
                      ? isTransparentBg
                        ? 'bg-white/20 text-white'
                        : 'bg-primary-100 text-primary-700'
                      : isTransparentBg
                        ? 'text-white/80 hover:text-white hover:bg-white/10'
                        : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-200'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <button
              className={`w-9 h-9 flex items-center justify-center rounded-full transition-all duration-200 ${
                isTransparentBg
                  ? 'text-white/80 hover:text-white hover:bg-white/10'
                  : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-200'
              }`}
              aria-label="搜索"
            >
              <i className="ri-search-line text-lg"></i>
            </button>
            <Link
              to="/about"
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                isTransparentBg
                  ? 'bg-white text-foreground-950 hover:bg-white/90'
                  : 'bg-primary-500 text-background-50 hover:bg-primary-600'
              }`}
            >
              关于我们
              <i className="ri-arrow-right-line ml-1.5 text-xs"></i>
            </Link>
          </div>

          <button
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-full"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="菜单"
          >
            <i className={`text-xl transition-colors duration-300 ${
              isTransparentBg && !mobileOpen ? 'text-white' : 'text-foreground-950'
            } ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200">
          <div className="px-4 py-4 flex flex-col gap-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === link.path
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-foreground-600 hover:bg-background-100'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/about"
              className="px-4 py-3 rounded-lg text-sm font-medium bg-primary-500 text-background-50 text-center mt-2"
            >
              关于我们
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}